import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * SearchableSelect — drop-in replacement for <Select> that lets the user
 * type to filter through long option lists (clients, services, etc.).
 *
 * Props mirror the shadcn Select where possible so swapping is one-liner.
 *
 *   <SearchableSelect
 *     value={clientId}
 *     onChange={setClientId}
 *     options={clients.map(c => ({ value: c.id, label: c.name, hint: c.phone }))}
 *     placeholder="اختر عميلاً..."
 *     searchPlaceholder="ابحث بالاسم أو الهاتف..."
 *   />
 *
 * Each option: { value: string, label: string, hint?: string, search?: string }
 * `search` (optional) lets the caller pre-compute a haystack string mixing
 * any extra fields (phone, code, email...) that should also be searchable.
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'اختر من القائمة...',
  searchPlaceholder = 'ابحث...',
  emptyText = 'لا توجد نتائج مطابقة',
  disabled = false,
  className = '',
  width = 'w-full',
  testId,
  allowClear = true,
}) {
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value],
  );

  // Normalize options once for fast filtering
  const enriched = React.useMemo(
    () => options.map((o) => ({
      ...o,
      _haystack: (o.search || `${o.label} ${o.hint || ''}`).toLowerCase(),
    })),
    [options],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`${width} justify-between font-normal text-right ${className}`}
        >
          <span className={`truncate ${!selected ? 'text-gray-500' : 'text-gray-900'}`}>
            {selected
              ? (selected.hint ? `${selected.label} — ${selected.hint}` : selected.label)
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        dir="rtl"
      >
        <Command
          filter={(itemValue, search) => {
            // Custom filter that matches our enriched haystack
            const item = enriched.find((o) => String(o.value) === itemValue);
            if (!item) return 0;
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return item._haystack.includes(q) ? 1 : 0;
          }}
        >
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder={searchPlaceholder}
              className="h-10 border-0 focus:ring-0"
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allowClear && value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange?.('');
                    setOpen(false);
                  }}
                  className="text-gray-500 italic"
                >
                  ✕ مسح الاختيار
                </CommandItem>
              )}
              {enriched.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={String(opt.value)}
                  onSelect={(currentValue) => {
                    onChange?.(currentValue === String(value) ? '' : currentValue);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={`ml-2 h-4 w-4 ${String(value) === String(opt.value) ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{opt.label}</div>
                    {opt.hint && (
                      <div className="text-xs text-gray-500 truncate">{opt.hint}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
